import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS } from '@/hooks/useLanguage';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

export default function LanguageSelector() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const current = languages.find((l) => l.code === i18n.language) ?? languages[0];

  function select(code: string) {
    i18n.changeLanguage(code);
    document.documentElement.lang = code;
    setOpen(false);

    const segments = location.pathname.split('/').filter(Boolean);
    let rest: string;
    if (segments.length && SUPPORTED_LANGS.includes(segments[0])) {
      rest = segments.slice(1).join('/');
    } else {
      rest = segments.join('/');
    }
    const target = code === 'en' ? (rest ? `/${rest}` : '/') : rest ? `/${code}/${rest}` : `/${code}`;
    navigate(target);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-background-300 hover:bg-background-100 whitespace-nowrap"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <i className="ri-global-line w-4 h-4 flex items-center justify-center"></i>
        <span>{current.label}</span>
        <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <ul
          className="absolute right-0 z-50 mt-2 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1 shadow-sm"
          role="listbox"
        >
          {languages.map((l) => (
            <li key={l.code}>
              <button
                onClick={() => select(l.code)}
                className={`flex w-full items-center justify-between px-4 py-2 text-sm text-left whitespace-nowrap transition-colors hover:bg-background-100 ${
                  l.code === i18n.language ? 'font-semibold text-primary-600' : 'text-foreground-700'
                }`}
                role="option"
                aria-selected={l.code === i18n.language}
              >
                {l.label}
                {l.code === i18n.language && (
                  <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}